import { useState, useRef, useEffect } from "react";
import { Share2, Copy, Check, Mail, MessageCircle, X as XIcon } from "lucide-react";
import { toast } from "sonner";

export default function ShareButton({ url, title = "Check this out on DealVest", text = "", variant = "primary", className = "" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const fullUrl = url.startsWith("http") ? url : `${typeof window !== "undefined" ? window.location.origin : ""}${url}`;
  const encUrl = encodeURIComponent(fullUrl);
  const encTitle = encodeURIComponent(title);
  const encText = encodeURIComponent(`${title}${text ? " — " + text : ""} ${fullUrl}`);

  const platforms = [
    { id: "whatsapp", label: "WhatsApp", url: `https://wa.me/?text=${encText}`, color: "#25D366" },
    { id: "linkedin", label: "LinkedIn", url: `https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}`, color: "#0A66C2" },
    { id: "x", label: "X (Twitter)", url: `https://twitter.com/intent/tweet?text=${encTitle}&url=${encUrl}`, color: "#000000" },
    { id: "facebook", label: "Facebook", url: `https://www.facebook.com/sharer/sharer.php?u=${encUrl}`, color: "#1877F2" },
    { id: "email", label: "Email", url: `mailto:?subject=${encTitle}&body=${encText}`, color: "#475569" },
    { id: "sms", label: "SMS", url: `sms:?body=${encText}`, color: "#0EA5E9" },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Copy failed"); }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title, text, url: fullUrl }); return true; }
      catch { return false; }
    }
    return false;
  };

  const onClick = async () => {
    const shared = await nativeShare();
    if (!shared) setOpen(true);
  };

  return (
    <div className="relative inline-block" ref={ref}>
      <button onClick={onClick}
        className={`dv-btn ${variant === "primary" ? "dv-btn-primary" : "dv-btn-secondary"} dv-btn-sm ${className}`}
        data-testid="share-button">
        <Share2 size={14} /> Share
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-72 bg-white rounded-2xl border border-slate-200 shadow-xl p-3 z-50" data-testid="share-popover">
          <div className="flex items-center justify-between mb-3 px-1">
            <span className="text-sm font-semibold text-slate-900">Share</span>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><XIcon size={16} /></button>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-3">
            {platforms.map(p => (
              <a key={p.id} href={p.url} target={p.id === "email" || p.id === "sms" ? undefined : "_blank"} rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid={`share-${p.id}`}
                className="flex flex-col items-center gap-1.5 p-3 rounded-xl hover:bg-slate-50 transition-colors">
                <span className="w-9 h-9 rounded-full flex items-center justify-center text-white" style={{ background: p.color }}>
                  {p.id === "whatsapp" || p.id === "sms" ? <MessageCircle size={16} /> : p.id === "email" ? <Mail size={16} /> : p.label[0]}
                </span>
                <span className="text-[11px] text-slate-600">{p.label}</span>
              </a>
            ))}
          </div>
          <button onClick={copy} className="w-full flex items-center gap-2 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 text-sm" data-testid="share-copy">
            {copied ? <Check size={15} className="text-emerald-600" /> : <Copy size={15} className="text-slate-600" />}
            <span className="font-mono text-xs truncate flex-1 text-left text-slate-600">{fullUrl}</span>
            <span className="text-xs font-semibold text-blue-600">{copied ? "Copied" : "Copy"}</span>
          </button>
        </div>
      )}
    </div>
  );
}
